import { useState } from 'react';
import { toast } from 'react-toastify';

import type { RequestSignResult } from '@api';

import type {
  ConnectButtonVariant} from '@shared/common/components/Buttons/ConnectButton';
import {
  ConnectButton
} from '@shared/common/components/Buttons/ConnectButton';
import { namedWeb3Errors } from '@shared/constants/errors';
import { ADD_WALLET_MESSAGE } from '@shared/constants/wallets';
import displayErrorToast, { normalizeError } from '@shared/helpers/displayErrorToast';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { suppressAccountChangeLogout } from '@shared/helpers/web3/hooks/useListenToAccountChange';
import type { RequestSignProps } from '@shared/helpers/web3/hooks/useWeb3';
import useWeb3 from '@shared/helpers/web3/hooks/useWeb3';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';
import useSelfDataCheckLocalStorage from '@shared/hooks/useSelfDataCheckLocalStorage';

import { useAddWalletMutation } from '@query/user';

import { WalletConnectType, WALLETS_CONFIG } from '../model/walletsConfig';
import { ConfirmConnectModal } from './ConfirmConnectModal';

type Props = {
  connectType: WalletConnectType.EVM | WalletConnectType.PASSKEY;
  variant: ConnectButtonVariant;
};

export const ConnectEvmWalletButton = ({ connectType, variant }: Props) => {
  const featureToggles = useFeatureToggles();
  const { setValue: setSelfDataChecksLocalStorage } = useSelfDataCheckLocalStorage();
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);

  const { requestSign, isLoading, setIsLoading } = useWeb3();
  const { mutateAsync: addWallet, isPending: isAdding } = useAddWalletMutation();
  const isDisabled = isLoading || isAdding;

  const resetState = () => {
    setIsLoading(false);
    setIsConnectModalOpen(false);
  };

  const handleSignResult = async (signResult?: RequestSignResult) => {
    if (!signResult) {
      return setIsLoading(false);
    }

    try {
      const { address, walletType, network } = signResult;

      await addWallet({ network, wallet: walletType, address });

      toast.success('Wallet successfully assigned to MagicID');
      resetState();
    } catch (error) {
      setIsLoading(false);

      const normalizedError: Error = normalizeError(error);

      if (normalizedError.message === namedWeb3Errors.SIGNATURE_VERIFICATION_HAS_FAILED) {
        throw normalizedError;
      }

      displayErrorToast({ error });
    }
  };

  const connectWallet = async () => {
    try {
      setIsLoading(true);

      const signProps: RequestSignProps = {
        walletType: connectType === WalletConnectType.EVM ? 'WalletConnect2' : 'Passkey',
        signType: 'SIWE',
        message: ADD_WALLET_MESSAGE,
      };

      suppressAccountChangeLogout();

      const signResult = await requestSign(signProps);

      await handleSignResult(signResult);

      setSelfDataChecksLocalStorage((oldData) => {
        return {
          ...oldData,
          hasEvmWallet: true,
        };
      });
    } catch (error) {
      resetState();
      displayWalletErrorToast({ error });
    }
  };

  const openConnectPopup = () => {
    if (featureToggles.humanityVerification) {
      setIsConnectModalOpen(true);
    } else {
      connectWallet();
    }
  };

  const { connectButtonText } = WALLETS_CONFIG[connectType];

  return (
    <>
      <ConnectButton disabled={isDisabled} variant={variant} onClick={() => openConnectPopup()}>
        {connectButtonText}
      </ConnectButton>
      <ConfirmConnectModal
        open={isConnectModalOpen}
        onOpenChange={setIsConnectModalOpen}
        onConfirm={() => {
          setIsConnectModalOpen(false);
          connectWallet();
        }}
      />
    </>
  );
};
